import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { LifeBuoy, Send } from 'lucide-react';
import {
  UniversalPage,
  Card,
  Button,
  Input,
  FormField,
  NotificationCard,
  UniversalLoadingState,
  useToast,
} from '@afrilink/ui';
import { ticketService } from '@/domains/support/services/ticketService';
import { ticketAttachmentService } from '@/domains/support/services/ticketAttachmentService';
import { AttachmentPicker } from '@/domains/support/components/TicketAttachments';
import type { Department, TicketPriority } from '@/domains/support/types/ticket.types';

export default function NewTicketPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  // RF-6 (2026-07-17): deep-link seeding — /app/support/new?dept=billing
  // &subject=…&body=… pre-fills the form (used by the invoice "Request refund"
  // quick action). dept matches the department NAME case-insensitively.
  const [searchParams] = useSearchParams();

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepts, setLoadingDepts] = useState(true);

  const [departmentId, setDepartmentId] = useState('');
  const [subject, setSubject] = useState(() => searchParams.get('subject') ?? '');
  const [body, setBody] = useState(() => searchParams.get('body') ?? '');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    let cancelled = false;
    ticketService
      .listDepartments()
      .then((d) => {
        if (cancelled) return;
        setDepartments(d);
        // Match EXACT case first, then fall back to case-insensitive.
        //
        // 2026-07-26: two active departments differ only by case — 'domains'
        // (1h SLA, the DT-1 domain-intent execute queue, populated by the
        // domain-intent-ticket EF) and 'Domains' (24h, general customer
        // questions). A purely case-insensitive `find` always resolved to
        // 'domains' because it sorts first, so ?dept=Domains silently filed
        // into the DT-1 queue — where a hand-created ticket is invisible to
        // the admin action queue (source 1 excludes name='domains'; source 5
        // requires workflow_state='awaiting_staff_execution', which a manual
        // ticket has no reason to carry).
        const raw = (searchParams.get('dept') ?? '').trim();
        const wanted = raw.toLowerCase();
        const seeded = raw
          ? (d.find((x) => x.name === raw) ?? d.find((x) => x.name.toLowerCase() === wanted))
          : undefined;
        const pick = seeded ?? d[0];
        if (!departmentId && pick) setDepartmentId(pick.id);
      })
      .finally(() => {
        if (!cancelled) setLoadingDepts(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit() {
    if (!departmentId || !subject.trim() || !body.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await ticketService.create({
        department_id: departmentId,
        subject: subject.trim(),
        body,
        priority,
      });
      if (file) {
        try {
          // Ticket-level attachment (message_id null) — ticket-create's
          // response does not expose the first message id.
          await ticketAttachmentService.attach(result.ticket_id, file);
        } catch (attachErr) {
          toast({
            tone: 'warning',
            title: 'Ticket submitted, but the attachment failed',
            body: attachErr instanceof Error ? attachErr.message : String(attachErr),
          });
        }
      }
      toast({
        tone: 'success',
        title: `Ticket ${result.ticket_number} submitted`,
        body: 'Our support team will reply within the department SLA.',
      });
      navigate(`/app/support/${result.ticket_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage
      title="New support ticket"
      description="Tell us what's going on and we'll get on it."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Support', href: '/app/support' },
        { label: 'New ticket' },
      ]}
    >
      {loadingDepts ? (
        <UniversalLoadingState variant="rows" count={4} />
      ) : (
        <Card interactive={false}>
          <div className="space-y-4 px-6 py-5">
            <FormField label="Department" htmlFor="department" required>
              <select
                id="department"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                disabled={submitting}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm"
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} (SLA {d.slaHours}h)
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Subject" htmlFor="subject" required>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={submitting}
                placeholder="Short summary of your issue"
                maxLength={250}
              />
            </FormField>

            <FormField label="Priority" htmlFor="priority">
              <select
                id="priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
                disabled={submitting}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm"
              >
                <option value="low">Low — no rush</option>
                <option value="normal">Normal</option>
                <option value="high">High — affecting service</option>
                <option value="urgent">Urgent — site or email down</option>
              </select>
            </FormField>

            <FormField
              label="Describe the issue"
              htmlFor="body"
              required
              helper="Include error messages, what you've tried, and when it started."
            >
              <textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={submitting}
                rows={8}
                maxLength={10000}
                className="w-full rounded border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
            </FormField>

            <AttachmentPicker file={file} onFileChange={setFile} disabled={submitting} />

            {error && (
              <NotificationCard
                tone="warning"
                title="Could not submit ticket"
                body={error}
              />
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/app/support')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !subject.trim() || !body.trim()}
                loading={submitting}
              >
                <Send className="h-4 w-4" />
                Submit ticket
              </Button>
            </div>
          </div>
        </Card>
      )}
    </UniversalPage>
  );
}
